import useStructuredTableColumnWidths from 'notebook/structured-tables/hooks/useStructuredTableColumnWidths';
import useStructuredTableColumnWidthStoreByStableKey from 'notebook/structured-tables/hooks/useStructuredTableColumnWidthStoreByStableKey';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import type {StructuredTableUpdateHandler} from 'notebook/structured-tables/StructuredTableOutsideNotebook';

// The storage key is this prefix plus the table kind, so every surface that renders a given kind
// (e.g. output tables) shares one set of column widths.
const COLUMN_WIDTHS_STORAGE_KEY_PREFIX = 'process-structured-table-column-widths:';

/**
 * Overlays a process task or output table with per-user column widths persisted in localStorage, and
 * routes resizes into that store while forwarding every other edit to the caller's update handler
 * unchanged. Keys widths by a stable per-column id so they survive the table being rebuilt (see
 * useStructuredTableColumnWidthStoreByStableKey).
 *
 * Spread the returned structuredTable and updateHandler into the table component and set
 * enableColumnResizing.
 */
export default function useResizableProcessStructuredTable({
  structuredTable,
  updateHandler,
  revertHandler,
}: {
  structuredTable: UnknownStructuredTable;
  updateHandler: StructuredTableUpdateHandler;
  revertHandler?: (structuredTable: UnknownStructuredTable) => void;
}): {
  structuredTable: UnknownStructuredTable;
  updateHandler: StructuredTableUpdateHandler;
  revertHandler?: (structuredTable: UnknownStructuredTable) => void;
} {
  const store = useStructuredTableColumnWidthStoreByStableKey({
    storageKey: `${COLUMN_WIDTHS_STORAGE_KEY_PREFIX}${structuredTable.kind}`,
    columns: structuredTable.columns,
  });
  return useStructuredTableColumnWidths({structuredTable, updateHandler, revertHandler, store});
}
